import { View, SxProp } from 'dripsy';
import React from 'react';

import { DonutChart } from '@/design-system/components/charts';
import { H5, TextCaption } from '@/design-system/components/typography/UniTypography';
import { DonutChartLegend } from '@/design-system/components/charts/donutChart/DonutChartLegend';
import { useDonutChart } from '@/design-system/components/charts/donutChart/useDonutChart.hook';
import { Icon } from '@/design-system/components/icons/Icon';
import { NetAssetValue } from '../../cards/NetAssetValue';
import { BaseCurrency } from 'store/types';
import { useStore } from 'store/store';
import { DonutChartItem } from '@/design-system/components/charts/donutChart/DonutChart';
import { SkeletonView } from '@/design-system/components/cards';
import { useTranslation } from 'react-i18next';

const DONUT_CHART_RADIUS = 150;

export interface OverviewProps {
  data: Array<DonutChartItem['item']>;
  lastUpdated: string;
  netAssetValue: string;
  currency: string;
  breakdown: Array<{
    amount: number;
    category: string;
    percentage: number;
  }>;
  loading: boolean;
  onChangeCurrency: (value: BaseCurrency) => void;
  showAmount?: boolean;
  toggleShowAmount?: (value: boolean) => void;
}

export const Overview: React.FC<OverviewProps> = ({
  data,
  netAssetValue,
  currency,
  lastUpdated,
  loading,
  onChangeCurrency,
  showAmount,
  toggleShowAmount
}) => {
  const { t } = useTranslation('earn');
  const store = useStore();

  const { chartData, legendData, selectArc } = useDonutChart({
    data,
    radius: DONUT_CHART_RADIUS,
    legendValueSuffix: currency
  });

  return (
    <View sx={styles.container}>
      {/* Top */}
      <View sx={styles.content}>
        {/* Header */}
        <H5 bold sx={{ alignSelf: 'flex-start' }}>
          {t('earn-invest::glossary.overview')}
        </H5>
        {/* Bottom */}
        <View sx={styles.bottom}>
          <DonutChart sx={styles.donutChart} donutChartData={chartData} radius={DONUT_CHART_RADIUS} />
          <View sx={styles.bottomRight}>
            <NetAssetValue
              netAssetValue={netAssetValue}
              sx={styles.netAssetValue}
              loading={loading}
              showAmount={showAmount}
              onChangeShowAmount={toggleShowAmount}
            />
            {loading && (
              <View sx={{ gap: '$1' }}>
                <SkeletonView height={16} width={200} />
                <SkeletonView height={16} width={150} />
                <SkeletonView height={16} width={180} />
              </View>
            )}
            {!loading && (
              <DonutChartLegend
                legendData={legendData}
                selectArc={selectArc}
                showValue={showAmount === undefined ? store.showAmount : showAmount}
              />
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

enum StyleName {
  assetSelectorGroup = 'assetSelectorGroup',
  bottom = 'bottom',
  bottomRight = 'bottomRight',
  container = 'container',
  content = 'content',
  donutChart = 'donutChart',
  lastUpdated = 'lastUpdated',
  lastUpdatedText = 'lastUpdatedText',
  netAssetvalue = 'netAssetValue',
  pageItemHeader = 'pageItemHeader',
  pageItemHeaderButton = 'pageItemHeaderButton',
  top = 'top'
}

const styles: Record<StyleName, SxProp> = {
  assetSelectorGroup: {
    alignItems: 'center'
  },
  bottom: {
    width: '100%',
    flex: 1,
    gap: '$5',
    alignItems: 'center'
  },
  bottomRight: {
    flex: 1,
    width: '100%'
  },
  container: {
    width: '$80',
    flex: 1,
    justifyContent: 'space-between'
  },
  content: {
    width: '100%',
    flex: 1,
    mb: '$6',
    gap: '$5',
    alignItems: 'center'
  },
  donutChart: {
    alignItems: 'center'
  },
  lastUpdated: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  lastUpdatedText: {
    ml: '$2',
    flexDirection: 'row',
    alignItems: 'center'
  },
  netAssetValue: { mb: '$5' },
  pageItemHeader: {
    mb: '$10'
  },
  pageItemHeaderButton: { paddingX: '$3' },
  top: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center'
  }
};
